import { HttpParams } from '@angular/common/http';

import type { DashboardTile, InputApiCallConfig } from './dvm-layout-builder.types';
import { isChartTile } from './dvm-tile-crossfilter.utils';

/**
 * Pure helpers behind an input control's "custom API call" and the recompute
 * fan-out: config normalisation, token substitution into a body template,
 * merging a custom response with the compute response, and picking the
 * tile a result entry belongs to.
 */

export function defaultApiCallConfig(): InputApiCallConfig {
  return {
    enabled: false,
    url: '',
    method: 'POST',
    bodyTemplate: '{\n  "value": "{value}",\n  "componentId": "{componentId}"\n}',
    mode: 'replace',
    targets: [],
  };
}

/** "When this control changes" choices. Module-level so the ng-select rows
 *  keep one array reference (ng-select reloads on every new `[items]`). */
export const API_CALL_MODE_OPTIONS: { value: InputApiCallConfig['mode']; label: string }[] = [
  { value: 'replace', label: 'Call ONLY this API (it answers for every target)' },
  { value: 'parallel', label: 'Call this API AND the normal compute API together' },
];

/** The same choices worded for a chart / table ("widget") in the design builder. */
export const WIDGET_API_CALL_MODE_OPTIONS: { value: InputApiCallConfig['mode']; label: string }[] = [
  { value: 'replace', label: 'Custom API only' },
  { value: 'parallel', label: 'Custom API + normal compute' },
];

export function sanitizeApiCallConfig(config: InputApiCallConfig): Record<string, unknown> {
  return {
    enabled: !!config.enabled,
    url: config.url?.trim() ?? '',
    method: config.method || 'POST',
    bodyTemplate: config.bodyTemplate ?? '',
    mode: config.mode === 'parallel' ? 'parallel' : 'replace',
    targets: (config.targets ?? []).map(String).filter(Boolean),
  };
}

export function restoreApiCallConfig(raw: unknown): InputApiCallConfig | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const source = raw as Record<string, unknown>;
  const fallback = defaultApiCallConfig();
  const method = String(source['method'] ?? fallback.method).toUpperCase();
  return {
    enabled: source['enabled'] === true,
    url: String(source['url'] ?? ''),
    method: (['GET', 'POST', 'PUT', 'PATCH'].includes(method)
      ? method
      : fallback.method) as InputApiCallConfig['method'],
    bodyTemplate: String(source['bodyTemplate'] ?? fallback.bodyTemplate),
    mode: source['mode'] === 'parallel' ? 'parallel' : 'replace',
    targets: Array.isArray(source['targets'])
      ? (source['targets'] as unknown[]).map(String).filter(Boolean)
      : [],
  };
}

/** The payload's targets minus the ones a custom API answers for. */
export function targetsExcluding(payload: Record<string, unknown>, excluded: Set<string>): Array<Record<string, unknown>> {
  const targets = Array.isArray(payload?.['targets']) ? (payload['targets'] as Array<Record<string, unknown>>) : [];
  return targets.filter((target) => !excluded.has(String(target?.['componentId'] ?? '')));
}

/** Custom entries win; compute entries for the custom-owned components are dropped. */
export function mergeComputeResponses(
  customResponse: Record<string, unknown> | null,
  computeResponse: Record<string, unknown> | null,
  customIds: Set<string>,
): Record<string, unknown> {
  const entriesOf = (response: Record<string, unknown> | null): Array<Record<string, unknown>> =>
    Array.isArray(response?.['results']) ? (response!['results'] as Array<Record<string, unknown>>) : [];
  const customEntries = entriesOf(customResponse);
  const ownedByCustom = new Set(
    customEntries.map((entry) => String(entry?.['componentId'] ?? '')).filter(Boolean),
  );
  const computeEntries = entriesOf(computeResponse).filter((entry) => {
    const id = String(entry?.['componentId'] ?? '');
    return !ownedByCustom.has(id) && !customIds.has(id);
  });
  return {
    ...(customResponse ?? {}),
    ...(computeResponse ?? {}),
    results: [...customEntries, ...computeEntries],
  };
}

/** Substitute `{token}` placeholders into the JSON body template; falls back to the raw tokens when the result is not JSON. */
export function buildApiCallBody(bodyTemplate: string | undefined, tokens: Record<string, unknown>, strict = false): unknown {
  const template = String(bodyTemplate ?? '').trim() || '{}';
  const filled = template
    .replace(/\[\s*"\{(\w+)\}"\s*\]/g, (match, key) =>
      (key in tokens && Array.isArray(tokens[key]) ? JSON.stringify(tokens[key]) : match))
    .replace(/"\{(\w+)\}"/g, (match, key) => (key in tokens ? JSON.stringify(tokens[key]) : match))
    .replace(/\{(\w+)\}/g, (match, key) => {
      if (!(key in tokens)) return match;
      const token = tokens[key];
      return token !== null && typeof token === 'object' ? JSON.stringify(token) : String(token ?? '');
    });
  try {
    return JSON.parse(filled);
  } catch {
    if (strict) throw new Error('Request body must be valid JSON after token substitution.');
    console.warn('[dvm] custom API body template is not valid JSON after token substitution; sending tokens instead:', filled);
    return { ...tokens };
  }
}

export function buildApiCallParams(tokens: Record<string, unknown>): HttpParams {
  let params = new HttpParams();
  Object.entries(tokens).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((entry) => { params = params.append(key, String(entry)); });
    } else {
      params = params.set(key, String(value));
    }
  });
  return params;
}

/** Current values of every OTHER input control on the dashboard (sent along with a recompute). */
export function otherActiveInputValues(tiles: DashboardTile[], excludeComponentId: string): Array<{ componentId: string; title: string; value: unknown }> {
  return tiles
    .filter((item) => !isChartTile(item) && item.layoutType !== 'FILE_UPLOAD' && item.layoutType !== 'POPUP' && item.componentId !== excludeComponentId)
    .map((item) => ({
      componentId: item.componentId,
      title: item.title || '',
      value: (item.payload as Record<string, unknown> | undefined)?.['value'] ?? item.inputControl?.value ?? null,
    }))
    .filter((entry) => !!entry.componentId);
}

/** Domain rule: changing the "SSA $" input also re-runs the "Price inflation %" and "Cost inflation %" inputs first. */
export function sideEffectInputsFor(tile: DashboardTile, tiles: DashboardTile[]): DashboardTile[] {
  const title = String(tile?.title ?? '').trim().toLowerCase();
  if (title !== 'ssa $') return [];
  const wanted = new Set(['price inflation %', 'cost inflation %']);
  return tiles.filter((item) => !isChartTile(item) && item.layoutType !== 'FILE_UPLOAD' && item.layoutType !== 'POPUP'
    && item.componentId !== tile.componentId
    && wanted.has(String(item.title ?? '').trim().toLowerCase()));
}

/** The tile a recompute result entry addresses: by componentId, else the single tile of the entry's chart type. */
export function findRecomputeTargetTile(allTiles: DashboardTile[], entryComponentId: string, chartType: string, allowedIds: Set<string>): DashboardTile | undefined {
  if (entryComponentId && (!allowedIds.size || allowedIds.has(entryComponentId))) {
    const direct = allTiles.find((tile) => tile.componentId === entryComponentId);
    if (direct) return direct;
  }

  if (!chartType) return undefined;
  const candidates = allowedIds.size
    ? allTiles.filter((tile) => allowedIds.has(String(tile.componentId ?? '')))
    : allTiles;
  const sameType = candidates.filter((tile) => tile.layoutType === chartType);
  return sameType.length === 1 ? sameType[0] : undefined;
}

/** Base64 payload of a picked file (data-URL prefix stripped). */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
